import React, { useState, useEffect, useRef } from 'react';
import { View, StyleSheet, TextInput, TouchableOpacity, Image, ScrollView, Alert, ActivityIndicator, Platform, Keyboard, Linking } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { HomeStackParamList } from '../navigation/HomeNavigator';
import { ClubsStackParamList } from '../types/navigation';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../theme/ThemeContext';
import { createTextPost, createImagePost, createPollPost, createLinkPost, createVideoPost, updatePost, Post } from '../lib/api/feed';
import { createClubPost, getClubById } from '../lib/api/clubs';
import { storage } from '../utils/storage';
import ThemedText from '../components/common/ThemedText';
import { showSuccessToast, showErrorToast } from '../utils/toast.native';
import { buildApiUrl, VIDEO_MIN_DURATION, VIDEO_MAX_DURATION } from '../config/env';
import MentionInput, { MentionUser } from '../components/common/MentionInput';
import VideoSourcePicker, { VideoSource } from '../components/feed/VideoSourcePicker';
import VideoProcessingDialog from '../components/feed/VideoProcessingDialog';
import VideoErrorDialog from '../components/feed/VideoErrorDialog';
import SelectedVideoPreview from '../components/feed/SelectedVideoPreview';
import { cleanupOptimizedVideo, inspectVideo, prepareVideoForUpload } from '../services/videoOptimization';
import { formatDurationSeconds, formatFileSize, validateVideoDuration } from '../utils/videoValidation';

type CreatePostNavigationProp = StackNavigationProp<HomeStackParamList, 'CreatePost'> | StackNavigationProp<ClubsStackParamList, 'CreatePost'>;
type CreatePostRouteProp = RouteProp<HomeStackParamList, 'CreatePost'> | RouteProp<ClubsStackParamList, 'CreatePost'>;

type AttachmentType = 'none' | 'image' | 'link' | 'poll' | 'video';

interface PollOption {
  id: string;
  text: string;
}

const MAX_TEXT_LENGTH = 500;

// Helper function to upload image to server
const uploadImageToServer = async (localUri: string): Promise<string> => {
  try {
    const formData = new FormData();
    const filename = localUri.split('/').pop() || 'image.jpg';
    const match = /\.(\w+)$/.exec(filename);
    const type = match ? `image/${match[1]}` : 'image/jpeg';

    console.log('📤 Uploading image:', { localUri, filename, type, platform: Platform.OS });

    // Handle web differently - need to fetch the blob and create a File
    if (Platform.OS === 'web') {
      console.log('🌐 Web upload - fetching blob from:', localUri);
      const response = await fetch(localUri);
      const blob = await response.blob();
      console.log('📦 Blob created:', { size: blob.size, type: blob.type });
      
      const file = new File([blob], filename, { type });
      console.log('📄 File created:', { name: file.name, size: file.size, type: file.type });
      
      formData.append('image', file);
      console.log('✅ File appended to FormData');
    } else {
      // React Native format
      formData.append('image', {
        uri: localUri,
        name: filename,
        type,
      } as any);
    }

    const token = await storage.getToken();
    if (!token) throw new Error('Not authenticated');

    // IMPORTANT: On web with FormData, we must let the browser set Content-Type with boundary
    // We can only set Authorization header
    const headers: any = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    console.log('🚀 Sending upload request to:', buildApiUrl('/api/upload/image'));
    console.log('📋 Headers:', Object.keys(headers));
    
    const response = await fetch(buildApiUrl('/api/upload/image'), {
      method: 'POST',
      headers,
      body: formData,
    });

    console.log('📥 Upload response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Upload failed:', errorText);
      throw new Error('Failed to upload image');
    }

    const data = await response.json();
    console.log('✅ Upload successful:', data.url);
    return data.url;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

// Helper function to upload video to server
const uploadVideoToServer = async (localUri: string): Promise<string> => {
  try {
    const formData = new FormData();
    const filename = localUri.split('/').pop() || 'video.mp4';
    const match = /\.(\w+)$/.exec(filename);
    const type = match ? `video/${match[1]}` : 'video/mp4';

    console.log('📤 Uploading video:', { localUri, filename, type, platform: Platform.OS });

    if (Platform.OS === 'web') {
      const response = await fetch(localUri);
      const blob = await response.blob();
      const file = new File([blob], filename, { type });
      formData.append('video', file);
    } else {
      formData.append('video', {
        uri: localUri,
        name: filename,
        type,
      } as any);
    }

    const token = await storage.getToken();
    if (!token) throw new Error('Not authenticated');

    const headers: any = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const uploadUrl = buildApiUrl('/api/upload/video');
    console.log('🚀 Sending video upload request to:', uploadUrl);

    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers,
      body: formData,
    });

    const contentType = response.headers.get('content-type') || '';
    const raw = await response.text();
    console.log('📥 Video upload response status:', response.status, contentType);

    let data: any;
    if (raw && (contentType.includes('application/json') || raw.trim().startsWith('{'))) {
      try {
        data = JSON.parse(raw);
      } catch {
        data = undefined;
      }
    }

    if (!response.ok) {
      const serverMessage = data?.error || data?.message;
      console.error('❌ Video upload failed:', raw.slice(0, 300));
      if (response.status === 404) {
        throw new Error('Failed to upload video. This endpoint is not available on the current server.');
      }
      throw new Error(serverMessage || 'Failed to upload video');
    }

    if (!data?.url) {
      throw new Error('Failed to upload video');
    }

    console.log('✅ Video upload successful:', data.url);
    return data.url;
  } catch (error) {
    console.error('Error uploading video:', error);
    throw error;
  }
};


const CreatePostScreen: React.FC = () => {
  const navigation = useNavigation<CreatePostNavigationProp>();
  const route = useRoute<CreatePostRouteProp>();
  const { user } = useAuth();
  const { theme } = useTheme();
  const params = route.params;
  const clubId = params?.clubId;
  const editingPost = params?.mode === 'edit' && params.post ? params.post : undefined;
  const isEditMode = Boolean(editingPost);
  const isClubPost = !!clubId;

  const initialText = editingPost
    ? editingPost.type === 'poll' && editingPost.poll
      ? editingPost.poll.question
      : editingPost.text || ''
    : '';

  const deriveInitialAttachmentType = (): AttachmentType => {
    if (!editingPost) return 'none';
    switch (editingPost.type) {
      case 'image':
        return 'image';
      case 'link':
        return 'link';
      case 'poll':
        return 'poll';
      case 'video':
        return 'video';
      default:
        return 'none';
    }
  };

  const initialPollOptions: PollOption[] = editingPost?.type === 'poll' && editingPost.poll
    ? editingPost.poll.options.map((option) => ({ id: option.id, text: option.text }))
    : [
        { id: `poll-${Date.now()}-1`, text: '' },
        { id: `poll-${Date.now()}-2`, text: '' },
      ];

  const [text, setText] = useState(initialText);
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);
  const [clubMembers, setClubMembers] = useState<MentionUser[]>([]);
  const [attachmentType, setAttachmentType] = useState<AttachmentType>(deriveInitialAttachmentType);
  
  // Image attachment
  const [imageUri, setImageUri] = useState<string | null>(() =>
    editingPost?.type === 'image' && editingPost.imageUrl ? editingPost.imageUrl : null
  );

  // Video attachment
  const [videoUri, setVideoUri] = useState<string | null>(() =>
    editingPost?.type === 'video' && (editingPost as any).videoUrl ? (editingPost as any).videoUrl : null
  );
  
  // Link attachment
  const [linkUrl, setLinkUrl] = useState(() => (editingPost?.type === 'link' && editingPost.linkUrl ? editingPost.linkUrl : ''));
  
  // Poll attachment
  const [pollOptions, setPollOptions] = useState<PollOption[]>(initialPollOptions);
  
  const canEditAttachments = !isEditMode;

  const [isPosting, setIsPosting] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [showVideoSourcePicker, setShowVideoSourcePicker] = useState(false);
  const [isPickingVideo, setIsPickingVideo] = useState(false);
  const [isProcessingVideo, setIsProcessingVideo] = useState(false);
  const [videoProcessingProgress, setVideoProcessingProgress] = useState(0);
  const [videoMetaLabel, setVideoMetaLabel] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<{
    title: string;
    message: string;
    actionLabel?: string;
    onAction?: () => void;
  } | null>(null);

  const optimizedTempUriRef = useRef<string | null>(null);
  const cancelOptimizationRef = useRef<(() => void) | null>(null);
  const isMountedRef = useRef(true);

  // Load club members for mentions (only for club posts)
  useEffect(() => {
    if (isClubPost && clubId) {
      loadClubMembers();
    }
  }, [isClubPost, clubId]);

  const loadClubMembers = async () => {
    try {
      const clubData = await getClubById(clubId!);
      const members: MentionUser[] = (clubData.members || []).map((member: any) => ({
        id: member.userId,
        name: member.user.name || 'Unknown',
        profession: member.user.profession,
        photo: member.user.photo,
      }));
      setClubMembers(members);
    } catch (error) {
      console.error('Error loading club members:', error);
    }
  };

  useEffect(() => {
    const TAB_BAR_HEIGHT = 60; // Approximate height of bottom tab navigator
    
    const keyboardWillShow = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow',
      (e) => {
        // Subtract tab bar height since it's behind the keyboard
        const adjustedHeight = Math.max(0, e.endCoordinates.height - TAB_BAR_HEIGHT);
        setKeyboardHeight(adjustedHeight);
      }
    );
    const keyboardWillHide = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide',
      () => {
        setKeyboardHeight(0);
      }
    );

    return () => {
      keyboardWillShow.remove();
      keyboardWillHide.remove();
    };
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cancelOptimizationRef.current?.();
      cleanupOptimizedVideo(optimizedTempUriRef.current);
      optimizedTempUriRef.current = null;
    };
  }, []);

  const showVideoError = (
    title: string,
    message: string,
    action?: { label: string; onPress: () => void }
  ) => {
    setVideoError({
      title,
      message,
      actionLabel: action?.label,
      onAction: action?.onPress,
    });
  };

  const clearSelectedVideo = async () => {
    const tempUri = optimizedTempUriRef.current;
    optimizedTempUriRef.current = null;
    setVideoUri(null);
    setVideoMetaLabel(null);
    if (tempUri) {
      await cleanupOptimizedVideo(tempUri);
    }
  };

  const handleCancel = () => {
    if (isEditMode) {
      if (text.trim() !== initialText.trim()) {
        if (Platform.OS === 'web') {
          const discard = window.confirm('Discard Changes\n\nAre you sure you want to discard your changes?');
          if (discard) navigation.goBack();
        } else {
          Alert.alert(
            'Discard Changes',
            'Are you sure you want to discard your changes?',
            [
              { text: 'Keep Editing', style: 'cancel' },
              { text: 'Discard', style: 'destructive', onPress: () => navigation.goBack() },
            ]
          );
        }
      } else {
        navigation.goBack();
      }
      return;
    }

    const hasDraftContent = Boolean(
      text.trim() ||
      attachmentType !== 'none' ||
      imageUri ||
      videoUri ||
      linkUrl ||
      pollOptions.some((option) => option.text.trim())
    );

    if (hasDraftContent) {
      if (Platform.OS === 'web') {
        const discard = window.confirm('Discard Post\n\nAre you sure you want to discard this post?');
        if (discard) navigation.goBack();
      } else {
        Alert.alert(
          'Discard Post',
          'Are you sure you want to discard this post?',
          [
            { text: 'Keep Editing', style: 'cancel' },
            { text: 'Discard', style: 'destructive', onPress: () => navigation.goBack() },
          ]
        );
      }
    } else {
      navigation.goBack();
    }
  };

  const handleSelectAttachmentType = (type: AttachmentType) => {
    if (!canEditAttachments) {
      Alert.alert('Attachments locked', 'You cannot change attachments while editing a post.');
      return;
    }
    if (attachmentType === type) {
      // Deselect if clicking the same type
      setAttachmentType('none');
      setImageUri(null);
      clearSelectedVideo();
      setLinkUrl('');
      setPollOptions([
        { id: `poll-${Date.now()}-1`, text: '' },
        { id: `poll-${Date.now()}-2`, text: '' },
      ]);
    } else {
      setAttachmentType(type);
      setImageUri(null);
      clearSelectedVideo();
      setLinkUrl('');
      setPollOptions([
        { id: `poll-${Date.now()}-1`, text: '' },
        { id: `poll-${Date.now()}-2`, text: '' },
      ]);
    }
  };

  const handlePickImage = async () => {
    if (!canEditAttachments) {
      Alert.alert('Attachments locked', 'You cannot change the image while editing a post.');
      return;
    }
    try {
      // Photo picker on Android 13+ uses system UI - no READ_MEDIA_* permissions needed

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        setImageUri(result.assets[0].uri);
      }
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to pick image. Please try again.');
    }
  };

  const handlePickVideo = async (source: VideoSource) => {
    if (!canEditAttachments) {
      Alert.alert('Attachments locked', 'You cannot change the video while editing a post.');
      return;
    }
    if (isPickingVideo || isProcessingVideo) {
      return;
    }

    setIsPickingVideo(true);

    try {
      if (source === 'camera') {
        const { status } = await ImagePicker.requestCameraPermissionsAsync();
        if (status !== 'granted') {
          showVideoError(
            'Camera permission needed',
            'Enable Camera (and Microphone) in Settings to record a video.',
            Platform.OS === 'web'
              ? undefined
              : { label: 'Open Settings', onPress: () => Linking.openSettings() }
          );
          return;
        }
      }

      const options: ImagePicker.ImagePickerOptions = {
        mediaTypes: ImagePicker.MediaTypeOptions.Videos,
        allowsEditing: false, // Set to false to avoid Web and Android crashes with video
        videoMaxDuration: VIDEO_MAX_DURATION,
        quality: 0.8,
      };

      const result = source === 'camera'
        ? await ImagePicker.launchCameraAsync(options)
        : await ImagePicker.launchImageLibraryAsync(options);

      if (result.canceled || !result.assets[0]) {
        return;
      }

      const pickedAsset = result.assets[0];
      if (!pickedAsset.uri) {
        showVideoError('Invalid video file', 'This video file could not be processed. Please try a different video.');
        return;
      }

      console.log('🎥 Picked Video Asset:', pickedAsset);

      const fallback = {
        duration: pickedAsset.duration,
        width: pickedAsset.width,
        height: pickedAsset.height,
        fileSize: pickedAsset.fileSize,
      };

      let inspected;
      try {
        inspected = await inspectVideo(pickedAsset.uri, fallback);
      } catch (error) {
        console.warn('[CreatePost] Unable to read video metadata:', error);
        showVideoError(
          'Unable to read video',
          'This video file could not be processed. Please try a different video.'
        );
        return;
      }

      const durationCheck = validateVideoDuration(inspected.duration);
      if (!durationCheck.ok) {
        showVideoError(durationCheck.title, durationCheck.message);
        return;
      }

      setIsProcessingVideo(true);
      setVideoProcessingProgress(0);

      const prepared = await prepareVideoForUpload({
        uri: pickedAsset.uri,
        fallback: {
          duration: inspected.duration,
          width: inspected.width,
          height: inspected.height,
          fileSize: inspected.sizeBytes,
        },
        onProgress: (progress) => {
          if (isMountedRef.current) {
            setVideoProcessingProgress(progress);
          }
        },
        registerCancel: (cancel) => {
          cancelOptimizationRef.current = cancel;
        },
      });

      cancelOptimizationRef.current = null;

      if (!isMountedRef.current) {
        if (prepared.status === 'success' && prepared.wasOptimized) {
          await cleanupOptimizedVideo(prepared.uri);
        }
        return;
      }

      if (prepared.status === 'cancelled') {
        return;
      }

      if (prepared.status === 'error') {
        showVideoError(prepared.title, prepared.message);
        return;
      }

      const previousTemp = optimizedTempUriRef.current;
      optimizedTempUriRef.current = prepared.wasOptimized ? prepared.uri : null;

      const durationLabel = formatDurationSeconds(prepared.optimized.duration);
      const sizeLabel = formatFileSize(prepared.optimized.sizeBytes);
      const metaParts = [durationLabel, sizeLabel].filter(Boolean);
      setVideoMetaLabel(metaParts.join(' · ') || null);
      setVideoUri(prepared.uri);

      if (previousTemp && previousTemp !== prepared.uri) {
        await cleanupOptimizedVideo(previousTemp);
      }
    } catch (error) {
      console.error('Error selecting video:', error);
      if (isMountedRef.current) {
        const errorMessage = error instanceof Error ? error.message.toLowerCase() : '';
        if (errorMessage.includes('permission')) {
          showVideoError(
            'Permission needed',
            source === 'camera'
              ? 'Enable Camera (and Microphone) in Settings to record a video.'
              : 'Allow photo library access in Settings to choose a video.',
            Platform.OS === 'web'
              ? undefined
              : { label: 'Open Settings', onPress: () => Linking.openSettings() }
          );
        } else {
          showVideoError('Could not select video', 'Failed to select video. Please try again.');
        }
      }
    } finally {
      cancelOptimizationRef.current = null;
      if (isMountedRef.current) {
        setIsPickingVideo(false);
        setIsProcessingVideo(false);
        setVideoProcessingProgress(0);
      }
    }
  };

  const handleVideoOptionPress = () => {
    if (!canEditAttachments || isPickingVideo || isProcessingVideo) {
      return;
    }
    Keyboard.dismiss();
    setShowVideoSourcePicker(true);
  };

  const handleVideoSourceSelect = (source: VideoSource) => {
    setShowVideoSourcePicker(false);
    setTimeout(() => {
      handlePickVideo(source);
    }, 250);
  };

  const handleCancelVideoOptimization = () => {
    cancelOptimizationRef.current?.();
  };

  const handleAddPollOption = () => {
    if (!canEditAttachments) {
      Alert.alert('Attachments locked', 'You cannot modify poll options while editing a post.');
      return;
    }
    if (pollOptions.length >= 4) {
      Alert.alert('Maximum Options', 'You can add up to 4 poll options.');
      return;
    }
    const newOption: PollOption = {
      id: `poll-${Date.now()}-${pollOptions.length + 1}`,
      text: '',
    };
    setPollOptions([...pollOptions, newOption]);
  };

  const handleRemovePollOption = (id: string) => {
    if (!canEditAttachments) {
      Alert.alert('Attachments locked', 'You cannot modify poll options while editing a post.');
      return;
    }
    if (pollOptions.length <= 2) {
      Alert.alert('Minimum Options', 'A poll must have at least 2 options.');
      return;
    }
    setPollOptions(pollOptions.filter(option => option.id !== id));
  };

  const handleUpdatePollOption = (id: string, optionText: string) => {
    if (!canEditAttachments) {
      return;
    }
    setPollOptions(pollOptions.map(option => 
      option.id === id ? { ...option, text: optionText } : option
    ));
  };

  const handlePost = async () => {
    // Check if posting is already in progress
    if (isPosting || isProcessingVideo || isPickingVideo) return;

    // Validation
    const trimmedText = text.trim();
    const trimmedLink = linkUrl.trim();

    const isTextOptional = attachmentType === 'image' || attachmentType === 'video' || attachmentType === 'link';
    if (!trimmedText && !isTextOptional) {
      Alert.alert('Post Text Required', 'Please write something to share with your network.');
      return;
    }

    if (trimmedText.length > MAX_TEXT_LENGTH) {
      Alert.alert(
        'Text Too Long',
        `Your post is ${trimmedText.length - MAX_TEXT_LENGTH} characters over the limit. Please shorten it to ${MAX_TEXT_LENGTH} characters or less.`
      );
      return;
    }

    if (isEditMode && trimmedText === initialText.trim()) {
      Alert.alert('No Changes', 'You haven\'t made any changes to this post.');
      return;
    }

    if (!isEditMode) {
      // Validate attachments
      if (attachmentType === 'image' && !imageUri) {
        Alert.alert('Image Required', 'Please upload an image or deselect the image option.');
        return;
      }

      if (attachmentType === 'video' && !videoUri) {
        Alert.alert('Video Required', 'Please upload a video or deselect the video option.');
        return;
      }

      if (attachmentType === 'link' && !trimmedLink) {
        Alert.alert('Link Required', 'Please enter a URL or deselect the link option.');
        return;
      }

      if (attachmentType === 'poll') {
        const filledOptions = pollOptions.filter(opt => opt.text.trim());
        if (filledOptions.length < 2) {
          Alert.alert(
            'Poll Options Required', 
            'A poll needs at least 2 options. Please fill in the options or deselect the poll.'
          );
          return;
        }

        const optionTexts = filledOptions.map(opt => opt.text.trim().toLowerCase());
        const uniqueOptions = new Set(optionTexts);
        if (uniqueOptions.size !== optionTexts.length) {
          Alert.alert('Duplicate Options', 'Poll options must be unique. Please remove or edit duplicate options.');
          return;
        }
      }
    }

    setIsPosting(true);

    try {
      const token = await storage.getToken();
      if (!token) {
        Alert.alert('Authentication Required', `Please sign in to ${isEditMode ? 'update' : 'create'} the post.`);
        setIsPosting(false);
        return;
      }

      if (isEditMode && editingPost) {
        console.log('[CreatePost] Updating post:', { postId: editingPost.id });
        await updatePost(token, editingPost.id, trimmedText);
        showSuccessToast('Post updated successfully!');
      } else {
        console.log('[CreatePost] Creating post:', { isClubPost, clubId, attachmentType, hasImage: !!imageUri, hasVideo: !!videoUri, hasLink: !!trimmedLink });

        if (isClubPost && clubId) {
          // Create club post
          if (attachmentType === 'image' && imageUri) {
            // Upload image first
            const imageUrl = await uploadImageToServer(imageUri);
            await createClubPost(clubId, {
              text: trimmedText,
              type: 'image',
              imageUrl,
              mentionedUserIds,
            });
          } else if (attachmentType === 'video' && videoUri) {
            // Upload video first
            const videoUrl = await uploadVideoToServer(videoUri);
            await createClubPost(clubId, {
              text: trimmedText,
              type: 'video',
              videoUrl,
              mentionedUserIds,
            });
          } else if (attachmentType === 'link' && trimmedLink) {
            await createClubPost(clubId, {
              text: trimmedText,
              type: 'link',
              linkUrl: trimmedLink,
              mentionedUserIds,
            });
          } else {
            // Text-only club post
            await createClubPost(clubId, {
              text: trimmedText,
              type: 'text',
              mentionedUserIds,
            });
          }
        } else {
          // Create regular feed post
          if (attachmentType === 'image' && imageUri) {
            await createImagePost(token, imageUri, trimmedText || undefined);
          } else if (attachmentType === 'video' && videoUri) {
            await createVideoPost(token, videoUri, trimmedText || undefined);
          } else if (attachmentType === 'poll') {
            const filledOptions = pollOptions.filter(opt => opt.text.trim());
            await createPollPost(
              token,
              {
                question: trimmedText,
                options: filledOptions.map(opt => opt.text.trim()),
              }
            );
          } else if (attachmentType === 'link' && trimmedLink) {
            await createLinkPost(token, trimmedLink, trimmedText || undefined);
          } else {
            // Text-only post
            await createTextPost(token, trimmedText);
          }
        }

        console.log('[CreatePost] Post created successfully');
        showSuccessToast(`${isClubPost ? 'Club post' : 'Post'} created successfully!`);
        const tempUri = optimizedTempUriRef.current;
        optimizedTempUriRef.current = null;
        if (tempUri) {
          cleanupOptimizedVideo(tempUri);
        }
      }

      setIsPosting(false);
      navigation.goBack();
    } catch (error) {
      console.error(`[CreatePost] Error ${isEditMode ? 'updating' : 'posting'}:`, error);
      showErrorToast(
        error instanceof Error 
          ? error.message 
          : `Failed to ${isEditMode ? 'update' : 'create'} post. Please try again.`
      );
      setIsPosting(false);
    }
  };

  const trimmedTextValue = text.trim();
  const hasAttachment = 
    (attachmentType === 'image' && !!imageUri) ||
    (attachmentType === 'video' && !!videoUri) ||
    (attachmentType === 'link' && !!linkUrl.trim());
  const isPostDisabled = (!trimmedTextValue && !hasAttachment) || (isEditMode && trimmedTextValue === initialText.trim());
  const remainingChars = MAX_TEXT_LENGTH - text.length;
  const isActionDisabled = isPostDisabled || isPosting || isProcessingVideo || isPickingVideo;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top']}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: theme.border }]}>
        <TouchableOpacity
          style={styles.cancelButton}
          onPress={handleCancel}
          disabled={isPosting || isProcessingVideo}
        >
          <ThemedText style={[styles.cancelText, { color: theme.textSecondary }]}>Cancel</ThemedText>
        </TouchableOpacity>
        
        <ThemedText style={styles.headerTitle}>
          {isEditMode ? 'Edit Post' : isClubPost ? 'Create Club Post' : 'Create Post'}
        </ThemedText>
        
        {/* Empty space to balance the header */}
        <View style={styles.headerRight} />
      </View>

      <ScrollView 
        style={styles.content} 
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {/* User Info */}
        <View style={[styles.userInfo, { borderBottomColor: theme.border }]}>
          {user?.photo ? (
            <Image source={{ uri: user.photo }} style={styles.avatar} />
          ) : (
            <View style={styles.avatar}>
              <Ionicons name="person-circle" size={48} color={theme.textSecondary} />
            </View>
          )}
          <View>
            <ThemedText style={styles.userName}>{user?.name || 'User'}</ThemedText>
            {user?.profession && (
              <ThemedText style={[styles.userProfession, { color: theme.textSecondary }]}>
                {user.profession}
              </ThemedText>
            )}
          </View>
        </View>

        {/* Text Input */}
        {isClubPost ? (
          <MentionInput
            value={text}
            onChangeText={(newText, mentionIds) => {
              setText(newText);
              setMentionedUserIds(mentionIds);
            }}
            users={clubMembers}
            placeholder="What's on your mind? Use @ to mention members"
            maxLength={MAX_TEXT_LENGTH}
            multiline
            numberOfLines={6}
            style={styles.textInput}
          />
        ) : (
          <TextInput
            style={[styles.textInput, { color: theme.text }]}
            placeholder="What's on your mind?"
            placeholderTextColor={theme.placeholder}
            multiline
            value={text}
            onChangeText={setText}
            maxLength={MAX_TEXT_LENGTH}
            autoFocus
          />
        )}

        {/* Character Count */}
        {text.length > 0 && (
          <ThemedText 
            style={[
              styles.charCount, 
              { color: remainingChars < 50 ? theme.error : theme.textTertiary }
            ]}
          >
            {remainingChars} characters remaining
          </ThemedText>
        )}

        {/* Attachment Type Selector */}
        <View style={[styles.attachmentSelector, { borderTopColor: theme.border }]}>
          <ThemedText style={[styles.attachmentTitle, { color: theme.textSecondary }]}>
            Add to your post (optional)
          </ThemedText>
          {isEditMode && (
            <ThemedText style={[styles.attachmentNote, { color: theme.textSecondary }]}>Existing attachments can’t be changed while editing.</ThemedText>
          )}
          <View style={styles.attachmentButtons}>
            <TouchableOpacity
              style={[
                styles.attachmentButton,
                { 
                  backgroundColor: attachmentType === 'image' ? theme.accent + '20' : theme.backgroundSecondary,
                  borderColor: attachmentType === 'image' ? theme.accent : theme.border,
                  opacity: canEditAttachments ? 1 : 0.5,
                }
              ]}
              onPress={() => handleSelectAttachmentType('image')}
              disabled={!canEditAttachments}
            >
              <Ionicons 
                name="image" 
                size={24} 
                color={attachmentType === 'image' ? theme.accent : theme.textSecondary} 
              />
              <ThemedText 
                style={[
                  styles.attachmentButtonText,
                  { color: attachmentType === 'image' ? theme.accent : theme.textSecondary }
                ]}
              >
                Image
              </ThemedText>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.attachmentButton,
                { 
                  backgroundColor: attachmentType === 'video' ? theme.accent + '20' : theme.backgroundSecondary,
                  borderColor: attachmentType === 'video' ? theme.accent : theme.border,
                  opacity: canEditAttachments ? 1 : 0.5,
                }
              ]}
              onPress={() => handleSelectAttachmentType('video')}
              disabled={!canEditAttachments}
            >
              <Ionicons 
                name="videocam" 
                size={24} 
                color={attachmentType === 'video' ? theme.accent : theme.textSecondary} 
              />
              <ThemedText 
                style={[
                  styles.attachmentButtonText,
                  { color: attachmentType === 'video' ? theme.accent : theme.textSecondary }
                ]}
              >
                Video
              </ThemedText>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.attachmentButton,
                { 
                  backgroundColor: attachmentType === 'link' ? theme.accent + '20' : theme.backgroundSecondary,
                  borderColor: attachmentType === 'link' ? theme.accent : theme.border,
                  opacity: canEditAttachments ? 1 : 0.5,
                }
              ]}
              onPress={() => handleSelectAttachmentType('link')}
              disabled={!canEditAttachments}
            >
              <Ionicons 
                name="link" 
                size={24} 
                color={attachmentType === 'link' ? theme.accent : theme.textSecondary} 
              />
              <ThemedText 
                style={[
                  styles.attachmentButtonText,
                  { color: attachmentType === 'link' ? theme.accent : theme.textSecondary }
                ]}
              >
                Link
              </ThemedText>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.attachmentButton,
                { 
                  backgroundColor: attachmentType === 'poll' ? theme.accent + '20' : theme.backgroundSecondary,
                  borderColor: attachmentType === 'poll' ? theme.accent : theme.border,
                  opacity: canEditAttachments ? 1 : 0.5,
                }
              ]}
              onPress={() => handleSelectAttachmentType('poll')}
              disabled={!canEditAttachments}
            >
              <Ionicons 
                name="bar-chart" 
                size={24} 
                color={attachmentType === 'poll' ? theme.accent : theme.textSecondary} 
              />
              <ThemedText 
                style={[
                  styles.attachmentButtonText,
                  { color: attachmentType === 'poll' ? theme.accent : theme.textSecondary }
                ]}
              >
                Poll
              </ThemedText>
            </TouchableOpacity>
          </View>
        </View>

        {/* Image Attachment */}
        {attachmentType === 'image' && (
          <View style={styles.attachmentContent}>
            {imageUri ? (
              <View style={styles.imageContainer}>
                <Image source={{ uri: imageUri }} style={styles.imagePreview} />
                {!isEditMode && (
                  <TouchableOpacity
                     style={styles.removeButton}
                     onPress={() => setImageUri(null)}
                  >
                    <Ionicons name="close-circle" size={32} color="#FFFFFF" />
                  </TouchableOpacity>
                )}
              </View>
            ) : canEditAttachments ? (
              <TouchableOpacity 
                style={[styles.uploadButton, { borderColor: theme.accent, backgroundColor: theme.backgroundSecondary }]} 
                onPress={handlePickImage}
              >
                <Ionicons name="cloud-upload-outline" size={48} color={theme.accent} />
                <ThemedText style={[styles.uploadText, { color: theme.accent }]}>
                  Tap to upload image
                </ThemedText>
              </TouchableOpacity>
            ) : (
              <ThemedText style={[styles.attachmentNote, { color: theme.textSecondary }]}>No image selected.</ThemedText>
            )}
            {isEditMode && (
              <ThemedText style={[styles.attachmentNote, { color: theme.textSecondary }]}>
                Image attachments can’t be changed while editing.
              </ThemedText>
            )}
          </View>
        )}

        {/* Video Attachment */}
        {attachmentType === 'video' && (
          <View style={styles.attachmentContent}>
            {videoUri ? (
              <SelectedVideoPreview
                key={videoUri}
                uri={videoUri}
                subtitle={videoMetaLabel || undefined}
                onRemove={!isEditMode ? clearSelectedVideo : undefined}
              />
            ) : canEditAttachments ? (
              <TouchableOpacity 
                style={[
                  styles.uploadButton,
                  {
                    borderColor: theme.accent,
                    backgroundColor: theme.backgroundSecondary,
                    opacity: isPickingVideo || isProcessingVideo ? 0.6 : 1,
                  },
                ]} 
                onPress={handleVideoOptionPress}
                disabled={isPickingVideo || isProcessingVideo}
              >
                <Ionicons name="videocam-outline" size={48} color={theme.accent} />
                <ThemedText style={[styles.uploadText, { color: theme.accent }]}>
                  Tap to choose video ({VIDEO_MIN_DURATION}-{VIDEO_MAX_DURATION}s)
                </ThemedText>
              </TouchableOpacity>
            ) : (
              <ThemedText style={[styles.attachmentNote, { color: theme.textSecondary }]}>No video selected.</ThemedText>
            )}
            {isEditMode && (
              <ThemedText style={[styles.attachmentNote, { color: theme.textSecondary }]}>
                Video attachments can’t be changed while editing.
              </ThemedText>
            )}
          </View>
        )}

        {/* Link Attachment */}
        {attachmentType === 'link' && (
          <View style={styles.attachmentContent}>
            <TextInput
              style={[
                styles.linkInput,
                { 
                  backgroundColor: theme.backgroundSecondary,
                  borderColor: theme.border,
                  color: theme.text 
                }
              ]}
              placeholder="Paste your link here (https://...)"
              placeholderTextColor={theme.placeholder}
              value={linkUrl}
              onChangeText={setLinkUrl}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="url"
              editable={canEditAttachments}
            />
            {isEditMode && (
              <ThemedText style={[styles.attachmentNote, { color: theme.textSecondary }]}>
                Link URL can’t be changed while editing.
              </ThemedText>
            )}
          </View>
        )}

        {/* Poll Attachment */}
        {attachmentType === 'poll' && (
          <View style={styles.attachmentContent}>
            {pollOptions.map((option, index) => (
              <View key={option.id} style={styles.pollOptionRow}>
                <TextInput
                  style={[
                    styles.pollOptionInput,
                    { 
                      backgroundColor: theme.backgroundSecondary,
                      borderColor: theme.border,
                      color: theme.text 
                    }
                  ]}
                  placeholder={`Option ${index + 1}`}
                  placeholderTextColor={theme.placeholder}
                  value={option.text}
                  onChangeText={(text) => handleUpdatePollOption(option.id, text)}
                  maxLength={50}
                  editable={canEditAttachments}
                />
                {pollOptions.length > 2 && canEditAttachments && (
                  <TouchableOpacity
                    style={styles.removePollButton}
                    onPress={() => handleRemovePollOption(option.id)}
                  >
                    <Ionicons name="close-circle" size={24} color={theme.error} />
                  </TouchableOpacity>
                )}
              </View>
            ))}
            
            {pollOptions.length < 4 && canEditAttachments && (
              <TouchableOpacity
                style={[styles.addPollButton, { borderColor: theme.accent }]}
                onPress={handleAddPollOption}
              >
                <Ionicons name="add-circle-outline" size={20} color={theme.accent} />
                <ThemedText style={[styles.addPollText, { color: theme.accent }]}>
                  Add Option (max 4)
                </ThemedText>
              </TouchableOpacity>
            )}

            {isEditMode && (
              <ThemedText style={[styles.attachmentNote, { color: theme.textSecondary }]}>
                Poll options can’t be changed while editing.
              </ThemedText>
            )}
          </View>
        )}
        
        {/* Post Button at Bottom */}
        <View style={styles.bottomButtonContainer}>
          <TouchableOpacity
            style={[
              styles.bottomPostButton,
              { backgroundColor: isActionDisabled ? theme.disabled : theme.accent }
            ]}
            onPress={handlePost}
            disabled={isActionDisabled}
            activeOpacity={0.8}
          >
            {isPosting ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <ThemedText 
                style={[
                  styles.bottomPostText,
                  { color: isActionDisabled ? theme.textSecondary : '#FFFFFF' }
                ]}
              >
                {isEditMode ? 'Update Post' : 'Post'}
              </ThemedText>
            )}
          </TouchableOpacity>
        </View>
        
        {/* Keyboard spacer - empty box with keyboard height */}
        <View style={{ height: keyboardHeight }} />
      </ScrollView>

      <VideoSourcePicker
        visible={showVideoSourcePicker}
        onSelect={handleVideoSourceSelect}
        onCancel={() => setShowVideoSourcePicker(false)}
      />
      <VideoProcessingDialog
        visible={isProcessingVideo}
        progress={videoProcessingProgress}
        onCancel={handleCancelVideoOptimization}
        cancellable
      />
      <VideoErrorDialog
        visible={!!videoError}
        title={videoError?.title || ''}
        message={videoError?.message || ''}
        onClose={() => setVideoError(null)}
        actionLabel={videoError?.actionLabel}
        onAction={
          videoError?.onAction
            ? () => {
                const action = videoError.onAction;
                setVideoError(null);
                action?.();
              }
            : undefined
        }
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  cancelButton: {
    paddingVertical: 8,
    paddingHorizontal: 4,
    width: 70,
  },
  headerRight: {
    width: 70,
  },
  postButton: {
    paddingVertical: 8,
    paddingHorizontal: 20,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 70,
    minHeight: 36,
  },
  cancelText: {
    fontSize: 16,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  postText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    marginRight: 12,
  },
  userName: {
    fontSize: 16,
    fontWeight: '600',
  },
  userProfession: {
    fontSize: 13,
    marginTop: 2,
  },
  textInput: {
    fontSize: 16,
    lineHeight: 24,
    paddingHorizontal: 16,
    paddingVertical: 16,
    minHeight: 120,
    textAlignVertical: 'top',
  },
  charCount: {
    fontSize: 12,
    paddingHorizontal: 16,
    marginTop: -8,
    marginBottom: 8,
  },
  attachmentSelector: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderTopWidth: 1,
    marginTop: 8,
  },
  attachmentTitle: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 12,
  },
  attachmentNote: {
    fontSize: 12,
    marginTop: 6,
  },
  attachmentButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  attachmentButton: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    borderWidth: 2,
  },
  attachmentButtonText: {
    fontSize: 12,
    fontWeight: '600',
    marginTop: 4,
  },
  attachmentContent: {
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  imageContainer: {
    position: 'relative',
    marginBottom: 16,
  },
  imagePreview: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: 12,
  },
  videoPlaceholderPreview: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderStyle: 'dashed',
    padding: 20,
  },
  removeButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 16,
  },
  uploadButton: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    borderWidth: 2,
    borderRadius: 12,
    borderStyle: 'dashed',
  },
  uploadText: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 12,
  },
  linkInput: {
    fontSize: 15,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1,
  },
  pollOptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  pollOptionInput: {
    flex: 1,
    fontSize: 15,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  removePollButton: {
    padding: 4,
  },
  addPollButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderWidth: 2,
    borderRadius: 12,
    borderStyle: 'dashed',
    marginTop: 4,
  },
  addPollText: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  bottomButtonContainer: {
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 16,
  },
  bottomPostButton: {
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bottomPostText: {
    fontSize: 17,
    fontWeight: '600',
  },
});

export default CreatePostScreen;
